// =============================================================
// PropertySelector  Phase 4
// Lets user select a Search Console property for their business
// Safe rendering for loading, error, empty, and loaded states
// =============================================================

import React, { useState, useEffect } from 'react';
import { SearchConsolePropertyDto, SearchConsolePropertyOption } from '../../lib/api';

interface Props {
  connectedProperty: SearchConsolePropertyDto | null;
  availableProperties?: SearchConsolePropertyOption[];
  onSelectProperty: (siteUrl: string) => Promise<void>;
  loading: boolean;
  error?: string | null;
}

export const PropertySelector: React.FC<Props> = ({
  connectedProperty,
  availableProperties = [],
  onSelectProperty,
  loading,
  error,
}) => {
  // Ensure safeProperties is strictly an array
  const safeProperties = Array.isArray(availableProperties) ? availableProperties : [];

  const [selectedUrl, setSelectedUrl] = useState<string>(() => {
    if (connectedProperty?.siteUrl) return connectedProperty.siteUrl;
    if (safeProperties.length > 0 && safeProperties[0]?.siteUrl) return safeProperties[0].siteUrl;
    return '';
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync selected URL whenever connectedProperty or loaded properties change
  useEffect(() => {
    if (connectedProperty?.siteUrl) {
      setSelectedUrl(connectedProperty.siteUrl);
    } else if (!selectedUrl && safeProperties.length > 0 && safeProperties[0]?.siteUrl) {
      setSelectedUrl(safeProperties[0].siteUrl);
    }
  }, [connectedProperty?.siteUrl, safeProperties, selectedUrl]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUrl) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await onSelectProperty(selectedUrl);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to select Search Console property');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="property-selector-box"
      style={{
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
      }}
    >
      <div style={{ marginBottom: '12px' }}>
        <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
          Select Search Console Property
        </h4>
        <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
          Choose which verified Google Search Console property to associate with this business.
        </p>
      </div>

      {loading ? (
        <div
          style={{
            padding: '12px',
            fontSize: '0.85rem',
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span
            className="spinner"
            style={{
              width: '14px',
              height: '14px',
              border: '2px solid #cbd5e1',
              borderTopColor: '#2563eb',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <span>Loading Search Console properties...</span>
        </div>
      ) : error ? (
        <div
          style={{
            padding: '12px 14px',
            background: '#fef2f2',
            borderRadius: '6px',
            border: '1px solid #fecaca',
            fontSize: '0.85rem',
            color: '#b91c1c',
          }}
        >
          <strong>Unable to load Search Console properties.</strong>
          <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#7f1d1d' }}>{error}</div>
        </div>
      ) : safeProperties.length === 0 ? (
        <div
          style={{
            padding: '12px 14px',
            background: '#fffbeb',
            borderRadius: '6px',
            border: '1px solid #fef3c7',
            fontSize: '0.85rem',
            color: '#92400e',
          }}
        >
          No Search Console properties found for this Google account. Please verify your website ownership in Google Search Console first.
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={selectedUrl}
              onChange={(e) => setSelectedUrl(e.target.value)}
              disabled={saving}
              className="form-input"
              style={{
                flex: 1,
                minWidth: '240px',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
              }}
            >
              {safeProperties.map((prop) => (
                <option key={prop.siteUrl} value={prop.siteUrl}>
                  {prop.siteUrl} ({prop.permissionLevel || 'siteOwner'})
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving || !selectedUrl || selectedUrl === connectedProperty?.siteUrl}
            >
              {saving
                ? 'Saving...'
                : connectedProperty?.siteUrl === selectedUrl
                ? 'Selected'
                : 'Save Property'}
            </button>
          </div>

          {connectedProperty && (
            <div
              style={{
                marginTop: '8px',
                fontSize: '0.78rem',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span></span> Currently active: <strong>{connectedProperty.siteUrl}</strong>{' '}
              ({connectedProperty.propertyType})
            </div>
          )}

          {saveSuccess && (
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#16a34a', fontWeight: 500 }}>
               Search Console property updated successfully!
            </div>
          )}

          {saveError && (
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#dc2626', fontWeight: 500 }}>
               {saveError}
            </div>
          )}
        </form>
      )}

      <div
        style={{
          marginTop: '12px',
          padding: '8px 10px',
          background: '#f1f5f9',
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: '#64748b',
        }}
      >
        <strong>Data Note:</strong> Search Console metrics reflect search-performance queries, clicks,
        impressions, and estimated positions. Google aggregates and anonymizes some queries to protect
        user privacy.
      </div>
    </div>
  );
};
